import { useTranslation } from 'react-i18next';
import { Upload, FileText, X, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { SimulationDocumentResponse } from '@/types/simulation';
import { translateDocumentFailure } from '@/utils/documentFailureMessage';

type RejectedUpload = { fileName: string; reason: string };

type Props = {
  existingDocs: SimulationDocumentResponse[];
  pendingFiles: File[];
  uploadingFiles: Set<string>;
  dragOver: boolean;
  rejectedUploads?: RejectedUpload[];
  /** Visual cue: count of existing+pending reached the cap. Drop zone is dimmed but still interactive. */
  atLimit?: boolean;
  /** Reactive fallback: the backend returned DOCUMENT_LIMIT_REACHED. */
  limitReached?: boolean;
  onDrop: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: () => void;
  onFileInput: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemovePending: (index: number) => void;
  onRemoveExisting: (doc: SimulationDocumentResponse) => void;
  onDismissRejected?: (fileName: string) => void;
  onDismissLimitReached?: () => void;
};

function formatSize(bytes: number): string {
  return bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    : `${(bytes / 1024).toFixed(0)} KB`;
}

type Bucket = 'processing' | 'ready' | 'review';

function bucketFor(status: SimulationDocumentResponse['processingStatus']): Bucket {
  if (status === 'Ready') return 'ready';
  if (status === 'Failed' || status === 'Rejected') return 'review';
  return 'processing';
}

export default function FileUploadField({
  existingDocs,
  pendingFiles,
  uploadingFiles,
  dragOver,
  rejectedUploads = [],
  atLimit = false,
  limitReached = false,
  onDrop,
  onDragOver,
  onDragLeave,
  onFileInput,
  onRemovePending,
  onRemoveExisting,
  onDismissRejected,
  onDismissLimitReached,
}: Props) {
  const { t } = useTranslation();
  // atLimit dims the drop zone and replaces the format hint with an explanatory
  // message, but the zone stays interactive — backend rejects overflow with the
  // reactive banner + inline rejected rows.
  const showReactiveLimitBanner = limitReached;

  return (
    <div>
      {showReactiveLimitBanner && (
        <div
          role="alert"
          className="mb-3 flex items-start gap-2 rounded-lg border border-amber-300/50 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-800/50 dark:bg-amber-900/20 dark:text-amber-300"
        >
          <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
          <p className="flex-1">{t('configurator.documentLimitError')}</p>
          {onDismissLimitReached && (
            <button
              type="button"
              onClick={onDismissLimitReached}
              className="shrink-0 text-amber-800/80 hover:text-amber-900 dark:text-amber-300/80 dark:hover:text-amber-200"
              aria-label={t('common.dismissAlert')}
            >
              <X className="size-3.5" />
            </button>
          )}
        </div>
      )}

      {/* Drop zone */}
      <div
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        aria-disabled={atLimit}
        className={`relative flex flex-col items-center justify-center rounded-xl border-2 border-dashed p-6 transition-colors ${
          atLimit
            ? 'border-amber-300/50 bg-amber-50/30 dark:border-amber-800/50 dark:bg-amber-900/10'
            : dragOver
              ? 'border-primary bg-primary/5'
              : 'border-border hover:border-primary/40'
        }`}
      >
        <Upload className={`mb-2 size-6 ${atLimit ? 'text-amber-600/70 dark:text-amber-400/70' : 'text-muted-foreground'}`} />
        <p className="text-sm text-muted-foreground">
          {t('configurator.dragFilesHere')}{' '}
          <label className="cursor-pointer font-medium text-primary hover:text-primary/80">
            {t('configurator.selectFiles')}
            <input
              type="file"
              multiple
              onChange={onFileInput}
              className="hidden"
            />
          </label>
        </p>
        <p
          className={`mt-1 text-xs ${
            atLimit ? 'font-medium text-amber-700 dark:text-amber-400' : 'text-muted-foreground/60'
          }`}
        >
          {atLimit ? t('configurator.documentLimitError') : t('configurator.fileFormats')}
        </p>
      </div>

      {/* File list */}
      {(existingDocs.length > 0 || pendingFiles.length > 0 || rejectedUploads.length > 0) && (
        <ul className="mt-3 space-y-2">
          {existingDocs.map((doc) => {
            const bucket = bucketFor(doc.processingStatus);
            return (
              <li
                key={doc.id}
                className="flex flex-col gap-1 rounded-lg border border-border bg-muted px-3 py-2"
              >
                <div className="flex items-center gap-3">
                  {bucket === 'processing' ? (
                    <Loader2 className="size-4 shrink-0 animate-spin text-sky-600 dark:text-sky-400" />
                  ) : bucket === 'ready' ? (
                    <CheckCircle2 className="size-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
                  ) : (
                    <AlertTriangle className="size-4 shrink-0 text-amber-600 dark:text-amber-400" />
                  )}
                  <span className="flex-1 truncate text-sm text-foreground">{doc.fileName}</span>
                  <StatusPill bucket={bucket} label={t(`documents.status.${bucket}`)} />
                  <span className="text-xs text-muted-foreground">{formatSize(doc.fileSize)}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-xs"
                    onClick={() => onRemoveExisting(doc)}
                    className="text-muted-foreground hover:text-destructive"
                    aria-label={t('common.removeFile')}
                  >
                    <X className="size-3.5" />
                  </Button>
                </div>
                {bucket === 'review' && doc.failureReason && (
                  <p className="pl-7 text-xs text-amber-700 dark:text-amber-400">
                    {translateDocumentFailure(t, doc.failureReason)}
                  </p>
                )}
              </li>
            );
          })}
          {pendingFiles.map((file, i) => {
            const fileKey = `${file.name}-${file.size}`;
            const isUploading = uploadingFiles.has(fileKey);
            return (
              <li
                key={`pending-${i}`}
                className="flex items-center gap-3 rounded-lg border border-dashed border-border bg-muted px-3 py-2"
              >
                {isUploading ? (
                  <Loader2 className="size-4 shrink-0 animate-spin text-primary" />
                ) : (
                  <FileText className="size-4 shrink-0 text-muted-foreground" />
                )}
                <span className="flex-1 truncate text-sm text-foreground">{file.name}</span>
                <span className="text-xs text-muted-foreground">{formatSize(file.size)}</span>
                {!isUploading && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-xs"
                    onClick={() => onRemovePending(i)}
                    className="text-muted-foreground hover:text-destructive"
                    aria-label={t('common.removeFile')}
                  >
                    <X className="size-3.5" />
                  </Button>
                )}
              </li>
            );
          })}
          {rejectedUploads.map((r) => (
            <li
              key={`rejected-${r.fileName}`}
              className="flex flex-col gap-1 rounded-lg border border-amber-300/50 bg-amber-50 px-3 py-2 dark:border-amber-800/50 dark:bg-amber-900/20"
            >
              <div className="flex items-center gap-3">
                <AlertTriangle className="size-4 shrink-0 text-amber-600 dark:text-amber-400" />
                <span className="flex-1 truncate text-sm text-foreground">{r.fileName}</span>
                <StatusPill bucket="review" label={t('documents.status.review')} />
                {onDismissRejected && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-xs"
                    onClick={() => onDismissRejected(r.fileName)}
                    className="text-muted-foreground hover:text-destructive"
                    aria-label={t('common.removeFile')}
                  >
                    <X className="size-3.5" />
                  </Button>
                )}
              </div>
              <p className="pl-7 text-xs text-amber-700 dark:text-amber-400">{r.reason}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const pillClass: Record<Bucket, string> = {
  processing: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400',
  ready: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  review: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
};

function StatusPill({ bucket, label }: { bucket: Bucket; label: string }) {
  return (
    <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${pillClass[bucket]}`}>
      {label}
    </span>
  );
}
